/*
 * progressReport — read-only derivations behind the Progress report.
 *
 * Hierarchy (from the Knowledge Hub): competency = hub root (AI, ML…), skill =
 * the root's topics (RAG, LLM…), topic = the skill's subtopics, concept = leaf.
 *
 * Every number comes from the engine's memory tree / snapshot or the browser's
 * interview history. Where the evidence is thin the functions return null so
 * the UI can say "Not enough data yet" instead of a misleading score.
 */
import { indexTree } from './paths';
import { focusOptions } from './interviewData';

export const MIN_EVIDENCE = 5;          // graded answers before a proficiency estimate
const DAY = 86400000;

export const leavesOf = (n) => (!n ? [] : !(n.children || []).length ? [n] : n.children.flatMap(leavesOf));

/**
 * Proficiency 0–100 — not just accuracy: accuracy (60%), depth reached on the
 * hub's foundational→nuance ladder (25%) and coverage of the node's concepts
 * (15%), discounted when practice is stale. null below MIN_EVIDENCE answers.
 */
export const proficiency = (n) => {
  if (!n || (n.attempts || 0) < MIN_EVIDENCE) return null;
  const acc = n.correct / n.attempts;
  const leaves = leavesOf(n);
  const practised = leaves.filter((l) => l.attempts > 0);
  const coverage = leaves.length ? practised.length / leaves.length : 1;
  const laddered = practised.filter((l) => (l.depth_max || 0) > 0);
  const depth = laddered.length ? laddered.reduce((s, l) => s + Math.min(1, (l.depth_reached || 0) / l.depth_max), 0) / laddered.length : acc;
  const d = n.last_seen_days ?? 0;
  const recency = d <= 14 ? 1 : d <= 30 ? 0.92 : 0.85;
  return Math.round(100 * recency * (0.6 * acc + 0.25 * depth + 0.15 * coverage));
};

export const STATUS = {
  strong: { label: 'Strong', cls: 'strong' },
  developing: { label: 'Developing', cls: 'developing' },
  needs: { label: 'Needs practice', cls: 'needs' },
  early: { label: 'Not enough data yet', cls: 'early' },
  not: { label: 'Not assessed', cls: 'not' },
};
export const statusOf = (n) => {
  if (!n || !(n.attempts > 0)) return 'not';
  const p = proficiency(n);
  if (p == null) return 'early';
  return p >= 75 ? 'strong' : p >= 55 ? 'developing' : 'needs';
};

/** Skills (with their memory) for a role, or every practised skill when there's no role. */
export const skillRows = ({ role, hub, memTree }) => {
  const mem = indexTree(memTree || []);
  const list = role ? focusOptions(role, hub) : (hub || []).flatMap((r) => (r.children || []).map((c) => ({ id: c.id, name: c.name })));
  return list.map((s) => {
    const m = mem[s.id];
    const topics = (m?.children || []);
    return {
      id: s.id, name: s.name, node: m || null,
      proficiency: proficiency(m), status: statusOf(m),
      attempts: m?.attempts || 0, correct: m?.correct || 0,
      accuracy: m?.attempts ? m.correct / m.attempts : null,
      last: m?.attempts ? m.last_seen_days : null,
      topicsCovered: topics.filter((t) => t.attempts > 0).length, topicsTotal: topics.length,
      topicsAttention: topics.filter((t) => ['needs'].includes(statusOf(t)) || t.status === 'fading').length,
      competency: String(s.id).split('.')[0],
    };
  });
};

/** Coverage of a role's skills. */
export const coverageOf = (rows) => ({
  total: rows.length,
  assessed: rows.filter((r) => r.proficiency != null).length,
  strong: rows.filter((r) => r.status === 'strong').length,
  needs: rows.filter((r) => r.status === 'needs').length,
  inProgress: rows.filter((r) => r.status === 'early' || r.status === 'developing').length,
  notAssessed: rows.filter((r) => r.status === 'not').length,
});

/**
 * Readiness 0–100 for the goal role: the average proficiency of assessed skills,
 * scaled by how much of the role is covered, blended 80/20 with the average AI
 * interview score for this role when there is one. null until at least a third
 * of the role's skills (and 2+) have enough evidence.
 */
export const readinessOf = ({ rows, interviews = [] }) => {
  const assessed = rows.filter((r) => r.proficiency != null);
  const need = Math.max(2, Math.ceil(rows.length / 3));
  if (assessed.length < need) {
    return { score: null, need, assessedN: assessed.length, nextToAssess: rows.filter((r) => r.proficiency == null).sort((a, b) => b.attempts - a.attempts).slice(0, 3) };
  }
  const coverage = assessed.length / rows.length;
  let score = (assessed.reduce((s, r) => s + r.proficiency, 0) / assessed.length) * (0.75 + 0.25 * coverage);
  const scored = interviews.filter((e) => e.score != null);
  if (scored.length) score = 0.8 * score + 0.2 * (scored.reduce((s, e) => s + e.score, 0) / scored.length);
  return { score: Math.round(score), coverage, assessedN: assessed.length, usesInterviews: scored.length > 0 };
};

/**
 * Evidence-backed gaps for a node. Only gap types the data can support:
 * knowledge (low accuracy), difficulty (good accuracy but stuck at the
 * foundational depth), recall (a due review on something once known).
 */
export const gapsOf = (n, due = []) => {
  if (!n || !(n.attempts > 0)) return [];
  const out = [];
  const acc = n.correct / n.attempts;
  if (n.attempts >= 3 && acc < 0.4) out.push({ type: 'Knowledge gap', why: `${n.correct} of ${n.attempts} answers correct` });
  const laddered = leavesOf(n).filter((l) => l.attempts > 0 && (l.depth_max || 0) >= 3);
  if (laddered.length && acc >= 0.6) {
    const r = laddered.reduce((s, l) => s + (l.depth_reached || 0) / l.depth_max, 0) / laddered.length;
    if (r < 0.34) out.push({ type: 'Difficulty gap', why: 'Accurate on foundational questions, but not yet past the first depth level' });
  }
  const d = due.find((x) => x.concept_id === n.id || String(x.concept_id).startsWith(`${n.id}.`));
  if (d && acc >= 0.5) out.push({ type: 'Recall check due', why: `Last practised ${Math.round(d.last_seen_days)} days ago — worth revisiting` });
  return out;
};

/** Strong topics with evidence. */
export const strengthsOf = (memTree) => {
  const out = [];
  (memTree || []).forEach((root) => (root.children || []).forEach((skill) => (skill.children || []).concat([skill]).forEach((t) => {
    if ((t.attempts || 0) < MIN_EVIDENCE) return;
    const acc = t.correct / t.attempts;
    if (acc < 0.75) return;
    const ev = [`${t.correct} of ${t.attempts} answers correct`];
    const lad = leavesOf(t).filter((l) => l.attempts > 0 && l.depth_max);
    if (lad.length) {
      const best = Math.max(...lad.map((l) => l.depth_reached || 0));
      ev.push(`Reached depth level ${best} of ${Math.max(...lad.map((l) => l.depth_max))}`);
    }
    if ((t.last_seen_days ?? 99) <= 14) ev.push('Practised in the last two weeks');
    out.push({ node: t, acc, evidence: ev });
  })));
  return out.sort((a, b) => b.acc - a.acc || b.node.attempts - a.node.attempts).slice(0, 4);
};

/** Areas to improve with their gap types (skills/topics with evidence). */
export const improvementsOf = (memTree, due) => {
  const out = [];
  (memTree || []).forEach((root) => (root.children || []).forEach((skill) => [skill, ...(skill.children || [])].forEach((t) => {
    const gaps = gapsOf(t, due);
    if (gaps.length) out.push({ node: t, gaps, acc: t.attempts ? t.correct / t.attempts : 0 });
  })));
  // prefer the more specific (deeper) node when a skill and its topic share a gap
  return out.sort((a, b) => a.acc - b.acc || b.node.attempts - a.node.attempts).slice(0, 4);
};

/** Accuracy by difficulty (hub depth: 1 easy, 2 medium, 3+ hard) from recent graded events. */
export const byDifficulty = (events) => {
  const g = { Easy: [0, 0], Medium: [0, 0], Hard: [0, 0] };
  let n = 0;
  (events || []).forEach((e) => {
    const d = Number(e.payload?.depth);
    if (!d || e.outcome == null) return;
    const k = d <= 1 ? 'Easy' : d === 2 ? 'Medium' : 'Hard';
    g[k][0] += 1; if (e.outcome.correct) g[k][1] += 1; n += 1;
  });
  return { n, rows: Object.entries(g).map(([k, [a, c]]) => ({ label: k, attempts: a, accuracy: a ? c / a : null })) };
};

/** Totals from the engine snapshot. */
export const totalsOf = (snap) => {
  const bt = snap?.by_type || {};
  const bs = snap?.by_surface || {};
  const attempted = ['mcq.answered', 'theory.answered', 'theory.skipped', 'flashcard.feedback', 'rapidfire.answered', 'dsa.submit']
    .reduce((s, k) => s + (bt[k] || 0), 0);
  const completed = Object.values(bs).reduce((s, v) => s + (v.attempts || 0), 0);
  // accuracy over graded answers only — flashcard self-ratings are recall, not accuracy
  const graded = ['mcq', 'theory', 'rapidfire', 'dsa'].reduce((a, k) => ({ n: a.n + (bs[k]?.attempts || 0), c: a.c + (bs[k]?.correct || 0) }), { n: 0, c: 0 });
  const sessions = (bt['tutor.session_started'] || 0) + (bt['rapidfire.round'] || 0) + (bt['voice.interview'] || 0) + (bt['session.planned'] || 0);
  return { attempted, completed, accuracy: graded.n ? graded.c / graded.n : null, gradedN: graded.n, sessions };
};

/** Memory counts from the knowledge state. */
export const memoryCounts = (snap) => {
  const ks = snap?.knowledge_state || {};
  const due = new Set((snap?.due_reviews || []).map((d) => d.concept_id));
  let recalled = 0; let reinforce = 0; let thin = 0;
  Object.entries(ks).forEach(([id, k]) => {
    if ((k.attempts || 0) < 2 || (k.confidence || 0) < 0.4) { if (!due.has(id)) thin += 1; return; }
    if (due.has(id) || (k.mastery || 0) < 0.5) reinforce += 1; else recalled += 1;
  });
  return { encountered: Object.keys(ks).length, recalled, reinforce: reinforce + [...due].filter((id) => !ks[id] || (ks[id].attempts || 0) < 2).length, thin };
};

/** Activity on one day (days-ago d): recent events that day + topics last practised then. */
export const dayDetail = (d, { events, memTree }) => {
  const start = Date.now() - (d + 1) * DAY;
  const end = Date.now() - d * DAY;
  const evs = (events || []).filter((e) => { const t = Date.parse(e.ts); return t > start && t <= end; });
  const topics = [];
  (memTree || []).forEach((r) => (r.children || []).forEach((s) => {
    if (s.attempts > 0 && s.last_seen_days != null && Math.floor(s.last_seen_days) === d) topics.push(s.name);
  }));
  const graded = evs.filter((e) => e.outcome != null);
  return {
    events: evs, topics,
    graded: graded.length, correct: graded.filter((e) => e.outcome.correct).length,
    sessions: evs.filter((e) => /session_started|rapidfire.round|voice.interview/.test(e.type)).length,
  };
};

/** Recurring weak topic across saved interviews (needs 2+ mentions). */
export const recurringInterviewGap = (history) => {
  const counts = {};
  (history || []).slice(0, 6).forEach((e) => {
    const seen = new Set();
    (e.report?.question_evaluations || []).forEach((q, i) => {
      const t = q.topic || e.report?.questions_meta?.[i]?.topic;
      if (t && (q.score || 0) < 70 && !seen.has(t)) { seen.add(t); counts[t] = (counts[t] || 0) + 1; }
    });
  });
  const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  return top && top[1] >= 2 ? { topic: top[0], times: top[1] } : null;
};
